import type { WireRevisionNotice, WireRevisionType } from '../../../api/types'
import { PortionMark } from '../common/PortionMark'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const REVISION_TYPE_LABELS: Record<WireRevisionType, string> = {
  'substantive-revision':     'Substantive Revision',
  'administrative-revision':  'Administrative Revision',
  'compliance-revision':      'Compliance Revision',
  'substantive-recall':       'Substantive Recall',
  'administrative-recall':    'Administrative Recall',
  'compliance-recall':        'Compliance Recall',
}

function fmtDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'long',
      timeStyle: 'short',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

// ─── RevisionNoticesBanner ────────────────────────────────────────────────────

interface Props {
  revisions: WireRevisionNotice[]
}

/**
 * Renders a prominent reader-facing banner listing all post-publication
 * revision notices. Placed at the top of the article, above the thumbnail.
 * Only rendered when at least one notice exists.
 */
export function RevisionNoticesBanner({ revisions }: Props) {
  if (!revisions.length) return null

  return (
    <div className="rounded border border-amber-7 bg-amber-2 px-5 py-4">
      <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-amber-11">
        Editor's Note{revisions.length > 1 ? 's' : ''}
      </p>
      <ul className="m-0 list-none space-y-3 p-0">
        {revisions.map((notice, i) => (
          <li key={i} className={i > 0 ? 'border-t border-amber-6 pt-3' : undefined}>
            <p className="mb-1 text-[10px] font-semibold text-amber-11">
              {REVISION_TYPE_LABELS[notice.type]} · {fmtDate(notice.revisedAt)}
            </p>
            <p className="article-body text-amber-12">
              {notice.descriptionClassification && (
                <PortionMark classification={notice.descriptionClassification} disseminationControls={notice.descriptionDisseminationControls} />
              )}
              {notice.description}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}
